import React from 'react'
import styled from 'styled-components'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import FilterDisplay from 'components/chart-v2/components/FilterDisplay'
import type {
  BaseChartIdentifier,
  ChartConstraint,
  ChartFilter,
  ConstraintArg,
} from 'components/chart-v2/model'
import YogiDivider from 'components/ui/YogiDivider'

type Constraint = ChartConstraint
const LEGACY_POST_PROJECT_HINT = 'post_project_hint'

const isLegacyPostProjectHintArg = (
  arg: NonNullable<ChartConstraint['args']>[number] | undefined,
): boolean =>
  arg === LEGACY_POST_PROJECT_HINT ||
  (typeof arg === 'object' &&
    !Array.isArray(arg) &&
    'dimension' in arg &&
    arg.dimension === LEGACY_POST_PROJECT_HINT)

const isLegacyPostProjectHintConstraint = (constraint: Constraint): boolean =>
  constraint.fn === 'in' && isLegacyPostProjectHintArg(constraint.args?.[0])

interface ChartInfoModalProps {
  open: boolean
  onClose: () => void
  identifier: BaseChartIdentifier
  filters?: ChartFilter[]
}

const ChartInfoModal: React.FC<ChartInfoModalProps> = ({
  open,
  onClose,
  identifier,
  filters,
}) => {
  // Use the new filters format if available, otherwise fall back to parsing constraints
  const shouldUseNewFilters = filters && filters.length > 0

  return (
    <PopoverModal
      title={
        <div style={{ color: '#28364f' }}>
          This chart was generated using these filters.{' '}
          <Subtitle>
            Want to make changes? Try prompts like &quot;Exclude promotional
            reviews&quot; or &quot;Shorten the time period to the last 6
            months&quot;
          </Subtitle>
        </div>
      }
      isOpen={open}
      onClose={onClose}
      footer={null}
      width={600}
      content={
        <>
          <YogiDivider
            style={{
              width: 'calc(100% + 40px)',
              marginLeft: -20,
              marginBottom: 16,
            }}
          />
          {shouldUseNewFilters ? (
            <FilterDisplay filters={filters} />
          ) : (
            // Fallback to old constraint parsing logic for backwards compatibility
            identifier?.constraints &&
            identifier.constraints.length > 0 && (
              <div>
                <InfoContent>
                  {identifier.constraints
                    .filter((constraint: Constraint) => {
                      if (isLegacyPostProjectHintConstraint(constraint)) {
                        return false
                      }
                      return true
                    })
                    .map(
                      (
                        constraint: Constraint,
                        index: number,
                        filteredArray: Constraint[],
                      ) => {
                        const isLastItem = index === filteredArray.length - 1

                        // Handle date range constraints
                        if (
                          constraint.fn === 'and' &&
                          constraint.args?.some(
                            (arg) =>
                              typeof arg === 'object' &&
                              'fn' in arg &&
                              (arg.fn === 'gte' || arg.fn === 'lte') &&
                              arg.args?.[0] === 'post_created_at',
                          )
                        ) {
                          let startDate: Date | null = null
                          let endDate: Date | null = null

                          constraint.args.forEach((arg) => {
                            if (typeof arg !== 'object' || !('fn' in arg))
                              return
                            const constraintArg = arg as ConstraintArg
                            if (
                              constraintArg.fn === 'gte' &&
                              constraintArg.args?.[0] === 'post_created_at'
                            ) {
                              const nestedArg = constraintArg.args[1] as
                                | ConstraintArg
                                | undefined
                              const timestamp = nestedArg?.args?.[0]
                              if (typeof timestamp === 'number') {
                                startDate = new Date(timestamp * 1000)
                              }
                            } else if (
                              constraintArg.fn === 'lte' &&
                              constraintArg.args?.[0] === 'post_created_at'
                            ) {
                              const nestedArg = constraintArg.args[1] as
                                | ConstraintArg
                                | undefined
                              const timestamp = nestedArg?.args?.[0]
                              if (typeof timestamp === 'number') {
                                endDate = new Date(timestamp * 1000)
                              }
                            }
                          })

                          const formatDate = (date: Date): string => {
                            return date.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          }

                          let dateRangeText = ''
                          if (startDate && endDate) {
                            dateRangeText = `${formatDate(startDate)} - ${formatDate(endDate)}`
                          } else if (startDate) {
                            dateRangeText = `From ${formatDate(startDate)}`
                          } else if (endDate) {
                            dateRangeText = `Until ${formatDate(endDate)}`
                          } else {
                            dateRangeText = 'Custom date filter applied'
                          }

                          return (
                            <div key={index} style={{ marginBottom: '20px' }}>
                              <div style={{ paddingBottom: 8 }}>
                                <strong>Date Range</strong>
                              </div>
                              <div>{dateRangeText}</div>
                              {!isLastItem && (
                                <YogiDivider style={{ marginTop: 20 }} />
                              )}
                            </div>
                          )
                        }

                        // Handle 'in' constraints (brand, theme, etc.)
                        if (constraint.fn === 'in' || constraint.fn === 'eq') {
                          const field = constraint.args?.[0]
                          let readableField = ''

                          // Handle complex field structures like coalesce functions
                          if (
                            typeof field === 'object' &&
                            !Array.isArray(field) &&
                            'fn' in field &&
                            field.fn === 'coalesce'
                          ) {
                            // For coalesce, use the first field as the primary field
                            const primaryField = field.args?.[0]
                            const fieldMappings: { [key: string]: string } = {
                              cluster_group: 'Theme',
                              cluster_topic: 'Theme',
                              product_brand: 'Brand',
                              product_category: 'Category',
                              product_name: 'Product',
                              theme: 'Theme',
                              source_type: 'Source Type',
                              country: 'Country',
                              region: 'Region',
                              sentiment: 'Sentiment',
                              rating: 'Rating',
                            }
                            readableField =
                              (typeof primaryField === 'string'
                                ? fieldMappings[primaryField]
                                : undefined) || 'Theme'
                          } else if (typeof field === 'string') {
                            // Handle simple string field names
                            const fieldMappings: { [key: string]: string } = {
                              product_brand: 'Brand',
                              product_hierarchy: 'Product',
                              theme: 'Theme',
                              cluster_topic: 'Theme',
                              cluster_group: 'Theme',
                              source_type: 'Source Type',
                              country: 'Country',
                              region: 'Region',
                              sentiment: 'Sentiment',
                              score: 'Rating',
                            }
                            readableField =
                              fieldMappings[field] ||
                              field
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (l: string) =>
                                  l.toUpperCase(),
                                )
                          } else {
                            readableField = 'Filter'
                          }

                          const values = constraint.args?.slice(1) ?? []

                          return (
                            <div key={index} style={{ marginBottom: '20px' }}>
                              <div style={{ paddingBottom: 8 }}>
                                <strong>{readableField}</strong>
                              </div>
                              <div>
                                {values.map((value) => (
                                  <span
                                    key={String(value)}
                                    style={{
                                      display: 'inline-block',
                                      marginRight: '8px',
                                      marginBottom: '8px',
                                      padding: '4px 8px',
                                      backgroundColor: '#EEEFF3',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    {String(value)}
                                  </span>
                                ))}
                              </div>
                              {!isLastItem && (
                                <YogiDivider style={{ marginTop: 20 }} />
                              )}
                            </div>
                          )
                        }

                        // Handle other constraint types
                        return (
                          <div key={index} style={{ marginBottom: '8px' }}>
                            <strong>Filter:</strong> {constraint.fn} constraint
                            applied
                          </div>
                        )
                      },
                    )}
                  {identifier.constraints.filter((constraint: Constraint) => {
                    if (isLegacyPostProjectHintConstraint(constraint)) {
                      return false
                    }
                    return true
                  }).length === 0 && (
                    <div style={{ fontStyle: 'italic', color: '#666' }}>
                      No additional filters applied
                    </div>
                  )}
                </InfoContent>
              </div>
            )
          )}
        </>
      }
    />
  )
}

export default ChartInfoModal

const Subtitle = styled.div`
  font-weight: 400;
  font-size: var(--Font-size-text-sm);
  color: #28364f;
  margin: 8px 0 16px;
`

const InfoContent = styled.div`
  border-radius: 6px;
  padding: 12px 0 0;
  font-size: 14px;
  color: #495057;
  overflow-y: auto;
`
